import React, { useState, useEffect } from 'react'
import { globalState } from '../../../../store'
import { usePost, useFetch, notify } from '../../../../components/component-items/helpers'
import { useParams } from 'react-router-dom'
import { BackgroundLayer } from './styles'
import { toast } from 'react-toastify'
import { usePermissions } from '../../../../hooks/usePermissions'

// Components
import GlobalStyles from '../../../../components/component-items/styles'
import { Form, Col, Row, InputGroup } from 'react-bootstrap'
import CardLoading from '../../../../components/component-items/loading-popover'

// Fontawesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBarcodeRead,
  faUsdCircle,
  faBoxUsd,
  faFileInvoiceDollar,
  faWeightHanging,
  faTags,
  faRuler,
} from '@fortawesome/pro-duotone-svg-icons'

// USPS Retail Ground pieces may measure up to 130 inches in combined length and girth
// https://www.ups.com/pt/en/support/shipping-support/shipping-dimensions-weight.page
const MAX_DIMENSIONS_WARNING = 60
// USPS and UPS Max is around 70 pounds https://www.ups.com/pt/en/support/shipping-support/shipping-dimensions-weight.page
const MAX_WEIGHT_WARNING = 1120

export const ProductVariantSpecifications = ({ id }: { id?: string | number }) => {
  const productVariant = id || useParams<{ id: string }>().id
  const [state, setState] = useState<any>({})
  const { hasAccess } = usePermissions()

  const {
    state: { csrf },
  } = globalState()

  const url = `/api/product/variant/${productVariant}/specifications/`
  const { response: resp, error, loaded, placeholder }: any = useFetch(url, {})

  const specs = loaded ? resp.specifications : {}
  const isBundle = loaded ? resp.isBundle : false
  const isPackaging = loaded ? resp.isPackaging : false
  const isKasePackaging = loaded ? resp.isKasePackaging : false
  const isWarehouse = loaded ? resp.isWarehouse : false
  const canManagePricing = isWarehouse && hasAccess('products', 'admin')
  useEffect(() => {
    if (loaded) {
      if (resp.error) {
        notify({ type: 'error', message: resp.error })
      } else {
        setState(specs)
      }
    }
  }, [loaded])

  const handleSubmit = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    const payload = Object.fromEntries(Object.entries(state).filter(([key, value]) => value !== specs[key]))
    if (!canManagePricing) {
      delete payload.price
      delete payload.wholesalePrice
      delete payload.customFee
    }

    const changed = (field: string) => field in payload
    const requiredTextFields = ['sku', 'scancode']
    if (requiredTextFields.some((field) => changed(field) && !String(payload[field] ?? '').trim())) {
      toast('SKU and scancode must have a value', { type: 'error' })
      return
    }

    const dimensionFields = ['weight', 'length', 'width', 'height']
    if (!isBundle && dimensionFields.some((field) => changed(field) && !(Number(payload[field]) > 0))) {
      toast('Changed dimensions must have a value greater than 0', { type: 'error' })
      return
    }

    const storedDimensionFields = ['storedLength', 'storedWidth', 'storedHeight']
    if (
      isPackaging &&
      !isBundle &&
      storedDimensionFields.some((field) => changed(field) && !(Number(payload[field]) > 0))
    ) {
      toast('Changed stored dimensions must have a value greater than 0', { type: 'error' })
      return
    }

    const showDimensionsWarning =
      (changed('length') && Number(payload.length) >= MAX_DIMENSIONS_WARNING) ||
      (changed('width') && Number(payload.width) >= MAX_DIMENSIONS_WARNING) ||
      (changed('height') && Number(payload.height) >= MAX_DIMENSIONS_WARNING)
    const showWeightWarning = changed('weight') && Number(payload.weight) >= MAX_WEIGHT_WARNING

    if (!isBundle && changed('price') && !(Number(payload.price) > 0)) {
      toast('Price must have a value greater than 0', { type: 'error' })
      return
    }

    if (canManagePricing && changed('wholesalePrice')) {
      const wholesalePrice = String(payload.wholesalePrice ?? '').trim()
      if ((isKasePackaging && !wholesalePrice) || (wholesalePrice && !(Number(wholesalePrice) >= 0.01))) {
        toast(
          isKasePackaging
            ? 'Kase packaging variants must have a wholesale price of at least $0.01'
            : 'Wholesale price must be blank or at least $0.01',
          { type: 'error' }
        )
        return
      }
    }

    if (!isWarehouse || Object.keys(payload).length === 0) {
      return
    }

    const resetOversizedChanges = () =>
      setState({
        ...state,
        ...(changed('weight') && Number(payload.weight) >= MAX_WEIGHT_WARNING ? { weight: specs.weight } : {}),
        ...(changed('length') && Number(payload.length) >= MAX_DIMENSIONS_WARNING ? { length: specs.length } : {}),
        ...(changed('width') && Number(payload.width) >= MAX_DIMENSIONS_WARNING ? { width: specs.width } : {}),
        ...(changed('height') && Number(payload.height) >= MAX_DIMENSIONS_WARNING ? { height: specs.height } : {}),
      })

    if (showDimensionsWarning || showWeightWarning) {
      notify({
        title: 'Dimensions or weight are bigger than expected',
        message: 'Are you sure that you want to save these specifications?',
        type: 'warning',
        onSubmit: () => usePost(`/api/product/variant/${productVariant}/specifications/`, payload, csrf),
        // @ts-ignore
        onDismiss: resetOversizedChanges,
        autoClose: 0,
        requestConfirmation: true,
      })
    } else {
      usePost(`/api/product/variant/${productVariant}/specifications/`, payload, csrf)
    }
  }

  return (
    <BackgroundLayer style={{ display: 'block', position: 'relative', padding: '1em' }}>
      <GlobalStyles.CardHeader style={{ padding: '0 12px' }}>
        <GlobalStyles.CardTitle>
          <h3>Specifications</h3>
        </GlobalStyles.CardTitle>
        <GlobalStyles.CardToolbar>
          {isWarehouse ? (
            <GlobalStyles.Button onClick={handleSubmit} style={{ height: 40, marginRight: '-0.5em' }}>
              Update
            </GlobalStyles.Button>
          ) : null}
        </GlobalStyles.CardToolbar>
      </GlobalStyles.CardHeader>
      <Form noValidate style={{ marginTop: '1em' }}>
        <Row>
          <Form.Group as={Col} md={'6'} className="required">
            <Form.Label>SKU</Form.Label>
            <InputGroup>
              <InputGroup.Text id="inputGroupPrepend">
                <FontAwesomeIcon icon={faTags} />
              </InputGroup.Text>
              <Form.Control
                required
                type="text"
                placeholder="SKU"
                value={state.sku || ''}
                disabled={!isWarehouse}
                onChange={(e) => setState({ ...state, sku: e.target.value })}
              />
              <Form.Control.Feedback type="invalid">Please enter a SKU</Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
          <Form.Group as={Col} md={'6'} className="required">
            <Form.Label>Scancode</Form.Label>
            <InputGroup>
              <InputGroup.Text id="inputGroupPrepend">
                <FontAwesomeIcon icon={faBarcodeRead} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Scancode"
                aria-describedby="inputGroupPrepend"
                required
                disabled={!isWarehouse}
                value={state.scancode || ''}
                onChange={(e) => setState({ ...state, scancode: e.target.value })}
              />
              <Form.Control.Feedback type="invalid">Please enter a scancode.</Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Row>
        {!isBundle ? (
          <Row>
            <Form.Group as={Col} md="3" className="required">
              <Form.Label>Weight (oz)</Form.Label>
              <InputGroup>
                <InputGroup.Text id="inputGroupPrepend">
                  <FontAwesomeIcon icon={faWeightHanging} />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder="Weight (oz)"
                  value={state.weight || ''}
                  onChange={(e) => setState({ ...state, weight: e.target.value })}
                  required
                  disabled={!isWarehouse}
                />
                <Form.Control.Feedback type="invalid">Please provide a valid Weight.</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
            <Form.Group as={Col} md="3" className="required">
              <Form.Label>Length (in)</Form.Label>
              <InputGroup>
                <InputGroup.Text id="inputGroupPrepend">
                  <FontAwesomeIcon icon={faRuler} />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder="Length (in)"
                  value={state.length || ''}
                  onChange={(e) => setState({ ...state, length: e.target.value })}
                  required
                  disabled={!isWarehouse}
                />
                <Form.Control.Feedback type="invalid">Please provide a valid Length.</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
            <Form.Group as={Col} md="3" className="required">
              <Form.Label>Width (in)</Form.Label>
              <InputGroup>
                <InputGroup.Text id="inputGroupPrepend">
                  <FontAwesomeIcon icon={faRuler} />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder="Width (in)"
                  value={state.width || ''}
                  onChange={(e) => setState({ ...state, width: e.target.value })}
                  required
                  disabled={!isWarehouse}
                />
                <Form.Control.Feedback type="invalid">Please provide a valid Width.</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
            <Form.Group as={Col} md="3" className="required">
              <Form.Label>Height (in)</Form.Label>
              <InputGroup>
                <InputGroup.Text id="inputGroupPrepend">
                  <FontAwesomeIcon icon={faRuler} />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder="Height (in)"
                  value={state.height || ''}
                  onChange={(e) => setState({ ...state, height: e.target.value })}
                  required
                  disabled={!isWarehouse}
                />
                <Form.Control.Feedback type="invalid">Please provide a valid Height.</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Row>
        ) : null}
        {isPackaging && !isBundle && (
          <Row>
            <Form.Group as={Col} md="4" className="required">
              <Form.Label>Stored Length (in)</Form.Label>
              <InputGroup>
                <InputGroup.Text id="inputGroupPrepend">
                  <FontAwesomeIcon icon={faRuler} />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder="Stored Length (in)"
                  value={state.storedLength || ''}
                  onChange={(e) => setState({ ...state, storedLength: e.target.value })}
                  required
                  disabled={!isWarehouse}
                />
                <Form.Control.Feedback type="invalid">Please provide a valid Stored Length.</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
            <Form.Group as={Col} md="4" className="required">
              <Form.Label>Stored Width (in)</Form.Label>
              <InputGroup>
                <InputGroup.Text id="inputGroupPrepend">
                  <FontAwesomeIcon icon={faRuler} />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder="Width (in)"
                  value={state.storedWidth || ''}
                  onChange={(e) => setState({ ...state, storedWidth: e.target.value })}
                  required
                  disabled={!isWarehouse}
                />
                <Form.Control.Feedback type="invalid">Please provide a valid Stored Width.</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
            <Form.Group as={Col} md="4" className="required">
              <Form.Label>Stored Height (in)</Form.Label>
              <InputGroup>
                <InputGroup.Text id="inputGroupPrepend">
                  <FontAwesomeIcon icon={faRuler} />
                </InputGroup.Text>
                <Form.Control
                  type="number"
                  placeholder="Height (in)"
                  value={state.storedHeight || ''}
                  onChange={(e) => setState({ ...state, storedHeight: e.target.value })}
                  required
                  disabled={!isWarehouse}
                />
                <Form.Control.Feedback type="invalid">Please provide a valid Stored Height.</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
          </Row>
        )}
        <Row>
          <Form.Group as={Col} md="4" className={canManagePricing ? 'required' : ''}>
            <Form.Label>Price</Form.Label>
            <InputGroup>
              <InputGroup.Text id="inputGroupPrepend">
                <FontAwesomeIcon icon={faUsdCircle} />
              </InputGroup.Text>
              <Form.Control
                type="number"
                placeholder="Price"
                value={state.price || ''}
                onChange={(e) => setState({ ...state, price: e.target.value })}
                required={canManagePricing}
                disabled={!canManagePricing}
              />
              <Form.Control.Feedback type="invalid">Please provide a valid price.</Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
          <Form.Group as={Col} md="4" className={isKasePackaging && canManagePricing ? 'required' : ''}>
            <Form.Label>Wholesale Price</Form.Label>
            <InputGroup>
              <InputGroup.Text id="inputGroupPrepend">
                <FontAwesomeIcon icon={faBoxUsd} />
              </InputGroup.Text>
              <Form.Control
                type="number"
                placeholder="Wholesale Price"
                value={state.wholesalePrice ?? ''}
                onChange={(e) => setState({ ...state, wholesalePrice: e.target.value })}
                disabled={!canManagePricing}
                required={isKasePackaging && canManagePricing}
                min="0.01"
                step="0.01"
              />
              <Form.Control.Feedback type="invalid">Please provide a valid wholesale price.</Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
          <Form.Group as={Col} md="4" controlId="validationCustom05">
            <Form.Label>Custom Processing Fee</Form.Label>
            <InputGroup>
              <InputGroup.Text id="inputGroupPrepend">
                <FontAwesomeIcon icon={faFileInvoiceDollar} />
              </InputGroup.Text>
              <Form.Control
                type="number"
                placeholder="Custom Fee"
                value={state.customFee || ''}
                onChange={(e) => setState({ ...state, customFee: e.target.value })}
                disabled={!canManagePricing}
              />
              <Form.Control.Feedback type="invalid">Please provide a valid Processing Fee.</Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
        </Row>
      </Form>
      {!loaded ? <CardLoading text={placeholder} error={error} /> : null}
    </BackgroundLayer>
  )
}
